export default String.raw`
/**
 * Blog module for managing a responsive Swiper slider on a blog page.
 * Automatically initializes or destroys the slider based on the window width.
 * 
 * @module Blog
 */
const Blog = (() => {
    /**
     * Creates a debounced version of the provided function, which delays its execution
     * until after the specified delay has elapsed since the last time it was invoked.
     * 
     * @param {Function} func - The function to debounce.
     * @param {number} delay - The number of milliseconds to delay the function execution.
     * @returns {Function} - A debounced version of the provided function.
     */
    const debounce = (func, delay) => {
        if (typeof func !== 'function') {
            throw new TypeError('Expected a function as the first argument');
        }
        if (typeof delay !== 'number' || delay < 0) {
            throw new TypeError('Expected a non-negative number as the delay');
        }

        let timeoutId = null;

        return (...args) => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(() => {
                timeoutId = null;
                func(...args);
            }, delay);
        };
    };

    const blogSwiperContainer = document.querySelector('.blog-swiper-js');

    if (!blogSwiperContainer) {
        return;
    }

    let blogSwiperInitialized = false;
    let blogSwiper;

    const initBlogSlider = () => {
        if (blogSwiperInitialized) {
            return;
        } 

        blogSwiper = new Swiper('.blog-swiper-js', {
            breakpoints: {
                768: {
                    spaceBetween: 24
                }
            },
            createElements: true,
            slidesPerView: "auto",
            spaceBetween: 16,
            on: {
                afterInit: () => { blogSwiperInitialized = true; }
            }
        });
    }

    /**
     * Initializes the blog slider if the window width is below a certain threshold.
     * Destroys the slider if it is already initialized and the window width exceeds the threshold.
     * 
     * @function init
     */
    const init = () => {
        if (window.innerWidth <= 1439) {
            initBlogSlider();
            return;
        }
        if (blogSwiperInitialized && blogSwiper) {
            blogSwiper.destroy(true, true);
            blogSwiperInitialized = false;
        }
    };

    init();
    window.addEventListener('resize', debounce(() => { init(); }, 200));

    return {
        init
    };
})();
`